// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Container } from "@hardwoods/App/Components/Container/Container";
import LazyImage, { LazyImageProps } from "@hardwoods/MobiusOverrides/LazyImage/LazyImage";
import Link, { LinkPresentationProps, LinkProps } from "@hardwoods/MobiusOverrides/Link";
import systemColors from "@hardwoods/XDL/PreStyle/colors";
import { fontConfig } from "@hardwoods/XDL/PreStyle/typography";
import color from "@hardwoods/XDL/Utilities/color";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface SlideModel {
    fields: {
        imageUrl: string;
        imageTitle: string;
        imageAlt: string;
        imageDescription: string;
        linkUrl: LinkFieldValue;
    };
}

const enum fields {
    isAuthenticated = "isAuthenticated",
    welcomeText = "welcomeText",
    subText = "subText",
    backgroundColor = "backgroundColor",
    slides = "slides",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.isAuthenticated]: boolean;
        [fields.welcomeText]: string;
        [fields.subText]: string;
        [fields.slides]: SlideModel[];
        [fields.backgroundColor]: string;
    };
}

export interface WelcomeBannerStyles {
    outerWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    container?: GridContainerProps;
    welcomeItem?: GridItemProps;
    welcomeTitle?: TypographyProps;
    subText?: TypographyProps;
    welcomeWrapper?: InjectableCss;
    welcomeTextWrapper?: InjectableCss;
    link: LinkPresentationProps;
    slidesItem?: GridItemProps;
    itemWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    descriptionWrapper?: InjectableCss;
    imageTitle?: LinkPresentationProps;
    imageSubTitle?: TypographyProps;
}

export const welcomeBannerStyles: WelcomeBannerStyles = {
    outerWrapper: {
        css: css`
            ${({ backgroundColor }: any) => css`
                background: ${color(backgroundColor || "common.ui1")};
            `}
        `,
    },
    innerWrapper: {
        css: css`
            &&& {
                padding-inline: 10px;
            }
        `,
    },
    container: {
        gap: 40,
        offsetCss: css`
            padding: 0;
            min-height: 200px;
            &&& {
                margin: 0;
                width: 100%;
            }
            ${xScreen.lte("md")} {
                height: auto;
            }
        `,
        css: css`
            &&& {
                max-width: 100%;
            }
        `,
    },
    welcomeItem: {
        width: [12, 12, 12, 4, 4],
    },
    welcomeWrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-start;
            ${({ isAuthenticated }: any) => css`
                margin-top: ${isAuthenticated && "10px"};
            `}
        `,
    },
    welcomeTextWrapper: {
        css: css`
            ${xScreen.lte("md")} {
                width: 100%;
            }
        `,
    },
    link: {
        color: "text.inverse",
        typographyProps: {
            size: 12,
            weight: "bold",
        },
        css: css`
            margin-top: 20px;
            background: ${color("primary.main")};
            border: 1px solid ${color("primary.dark")};
            border-radius: 4px;
            padding: 7px 15px;
            display: block;
            width: 160px;
            height: 40px;
            text-align: center;
            ${xScreen.lte("sm")} {
                width: 100%;
            }
        `,
    },
    slidesItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            > div {
                width: 100%;
                height: 100%;
                display: flex;
                justify-content: center;
                align-items: normal;
                margin: auto 0;
                ${xScreen.lte("md")} {
                    flex-direction: column;
                    > div {
                        padding: 0 20px;
                        margin-bottom: 10px;
                        &:last-child {
                            margin-bottom: 0;
                        }
                    }
                }
            }

            ${xScreen.gt("xl")} {
                > div {
                    max-width: 1224px;
                }
            }
        `,
    },
    itemWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            background: ${color("common.background")};
            border-radius: 4px;
            padding: 15px 10px;

            ${xScreen.lte("md")} {
                flex-direction: row;
                width: 100%;
                justify-content: flex-start;
            }

            ${({ isAuthenticated }: any) => css`
                width: ${isAuthenticated ? "168px" : "180px"};
                margin-right: ${isAuthenticated ? "16px" : "44px"};
            `}
        `,
    },
    imageWrapper: {
        css: css`
            width: 80px;
            height: 80px;
            margin-bottom: 10px;
        `,
    },
    descriptionWrapper: {
        css: css`
            ${xScreen.lte("md")} {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                margin-left: 10px;
            }
        `,
    },
    welcomeTitle: {
        as: "h1",
        weight: 900,
        fontFamily: fontConfig.headingFontFamily,
        css: css`
            margin-bottom: 10px;
            line-height: 32px;
        `,
    },
    subText: {
        weight: 900,
        fontFamily: fontConfig.headingFontFamily,
        css: css`
            line-height: 20px;
        `,
    },
    image: {
        width: "80px",
        height: "80px",
        css: css`
            max-height: 100%;
            max-width: 100%;
            img {
                height: auto;
                width: auto;
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
    imageTitle: {
        color: "primary.main",
        typographyProps: {
            size: 15,
            weight: "bold",
        },
        css: css`
            margin-top: 5px;
            letter-spacing: 0;
            line-height: 20px;
            ${xScreen.lte("md")} {
                padding: 10px;
                text-align: left;
            }
        `,
    },
    imageSubTitle: {
        as: "p",
        fontFamily: fontConfig.headingFontFamily,
        css: css`
            letter-spacing: 0;
            line-height: 18px;
            padding: 10px;
            ${xScreen.lte("md")} {
                text-align: left;
            }
        `,
    },
};

type Props = OwnProps;

const WelcomeBanner: FC<Props> = ({ fields }) => {
    const { firstName, lastName, signInUrl, myAccountPageUrl } = useAppSelector(state => ({
        firstName: getSession(state).firstName,
        lastName: getSession(state).lastName,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url ?? "",
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url ?? "",
    }));

    const styles = welcomeBannerStyles;
    const slides = fields.slides;

    const welcomeTextElement = (subText?: string) => (
        <StyledWrapper {...styles.welcomeTextWrapper}>
            <Typography {...styles.welcomeTitle} size={fields.isAuthenticated ? 16 : 26}>
                {fields.welcomeText}
            </Typography>
            {subText && (
                <Typography {...styles.subText} size={fields.isAuthenticated ? 26 : 16}>
                    {subText}
                </Typography>
            )}
            {!fields.isAuthenticated && (
                <Link href={signInUrl} {...styles.link}>
                    {translate("Sign In / Register")}
                </Link>
            )}
            {fields.isAuthenticated && (
                <Link href={myAccountPageUrl} {...styles.link}>
                    {translate("Visit Your Dashboard")}
                </Link>
            )}
        </StyledWrapper>
    );

    return (
        <StyledWrapper {...styles.outerWrapper} {...{ backgroundColor: fields.backgroundColor }}>
            <Container {...styles.innerWrapper}>
                <GridContainer {...styles.container}>
                    <GridItem {...styles.welcomeItem}>
                        <StyledWrapper {...styles.welcomeWrapper} {...{ isAuthenticated: fields.isAuthenticated }}>
                            {fields.isAuthenticated
                                ? welcomeTextElement(`${firstName} ${lastName}`)
                                : welcomeTextElement(fields.subText)}
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.slidesItem}>
                        <StyledWrapper>
                            {slides?.map((slide, index) => {
                                return (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <StyledWrapper
                                        key={index}
                                        {...styles.itemWrapper}
                                        {...{ isAuthenticated: fields.isAuthenticated }}
                                    >
                                        <StyledWrapper {...styles.imageWrapper}>
                                            {slide.fields.imageUrl && (
                                                <LazyImage
                                                    src={slide.fields.imageUrl}
                                                    altText={slide.fields.imageAlt}
                                                    cloudinaryTransformations={{ width: 80, quality: "good" }}
                                                    {...styles.image}
                                                />
                                            )}
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.descriptionWrapper}>
                                            {fields.isAuthenticated && slide.fields.imageTitle && (
                                                <Link linkField={slide.fields.linkUrl} {...styles.imageTitle}>
                                                    {slide.fields.imageTitle}
                                                </Link>
                                            )}
                                            {slide.fields.imageDescription && (
                                                <Typography
                                                    {...styles.imageSubTitle}
                                                    size={fields.isAuthenticated ? 12 : 14}
                                                    weight={fields.isAuthenticated ? "normal" : 900}
                                                >
                                                    {slide.fields.imageDescription}
                                                </Typography>
                                            )}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                );
                            })}
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </Container>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: WelcomeBanner,
    definition: {
        displayName: "Welcome Banner",
        group: "Xngage Custom" as any,
        icon: "Carousel",
        fieldDefinitions: [
            {
                name: fields.isAuthenticated,
                displayName: "Is Authenticated?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.welcomeText,
                displayName: "Welcome Text",
                editorTemplate: "TextField",
                defaultValue: "Welcome",
                fieldType: "General",
            },
            {
                name: fields.subText,
                displayName: "Sub Text",
                editorTemplate: "TextField",
                defaultValue: "Here are a few useful shortcuts for you",
                fieldType: "General",
            },
            {
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                defaultValue: (systemColors.common as any).secondary2,
                fieldType: "General",
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                hideAdd: false,
                getDisplay: (item: HasFields) => {
                    return item.fields.imageTitle || item.fields.imageDescription;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "imageUrl",
                        displayName: "Image Url",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "imageAlt",
                        displayName: "Image Alt",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "imageTitle",
                        displayName: "Image Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "imageDescription",
                        displayName: "Image Description",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "linkUrl",
                        displayName: "Link Url",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Url", value: "" },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
